import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Logo from '../components/Logo'

type View = 'step1' | 'step2' | 'step3' | 'done' | 'trouble'

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button onClick={() => {
      navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }} style={{
      padding: '4px 10px', borderRadius: 5, fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.15s',
      background: copied ? 'rgba(16,185,129,0.15)' : 'rgba(124,58,237,0.1)',
      border: `1px solid ${copied ? 'rgba(16,185,129,0.3)' : 'rgba(124,58,237,0.2)'}`,
      color: copied ? '#10b981' : '#a78bfa',
    }}>{copied ? 'Copied!' : 'Copy'}</button>
  )
}

export default function ConnectPage() {
  const navigate = useNavigate()
  const [view, setView] = useState<View>('step1')
  const stepNum = view === 'step1' ? 1 : view === 'step2' ? 2 : view === 'step3' ? 3 : 0

  const pill = (n: number, label: string) => {
    const active = stepNum === n
    const done = view === 'done' || (stepNum > 0 && n < stepNum)
    const allDone = view === 'done'
    return (
      <div style={{ flex: 1, textAlign: 'center', padding: '6px 8px', borderRadius: 7, fontSize: 11, fontWeight: active || done || allDone ? 700 : 600, transition: 'all 0.2s',
        background: active ? 'rgba(124,58,237,0.15)' : (done || allDone) ? 'rgba(16,185,129,0.12)' : 'transparent',
        color: active ? '#a78bfa' : (done || allDone) ? '#10b981' : '#2d3f5c',
      }}>{label}</div>
    )
  }

  const S = { card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, animation: 'fadeIn 0.3s ease both' } as React.CSSProperties }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text1)' }}>
      <nav style={{ height: 52, background: 'var(--surface)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', padding: '0 20px', gap: 10, position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ cursor: 'pointer' }} onClick={() => navigate('/')}><Logo /></div>
        <div style={{ flex: 1 }} />
        <button onClick={() => navigate('/')} className="btn-ghost" style={{ fontSize: 11 }}>&larr; Back to dashboard</button>
      </nav>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: '40px 24px' }}>
        <div style={{ marginBottom: 8, fontSize: 11, fontWeight: 700, color: 'var(--brand-light)', textTransform: 'uppercase', letterSpacing: 0.8 }}>Connect to Claude</div>
        <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: -0.4, marginBottom: 6 }}>Use your skills inside Claude</h1>
        <p style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 24 }}>Connect Skilflow to Claude.ai in 3 steps. Once connected, Claude can read, create, and update your skills directly in conversation.</p>

        {/* Pills */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 24, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 4 }}>
          {pill(1, 'Step 1 of 3')}{pill(2, 'Step 2 of 3')}{pill(3, 'Step 3 of 3')}
        </div>

        {/* Step 1 */}
        {view === 'step1' && (
          <div style={S.card}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 20 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#a78bfa', flexShrink: 0 }}>1</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3 }}>Open Claude.ai Settings</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5 }}>Navigate to the Integrations section where you can add external tools</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
              {[
                ['1', <>Go to <strong>claude.ai</strong> and sign in</>],
                ['2', <>Click your <strong>profile picture</strong> in the top right corner</>],
                ['3', <>Go to <strong>Settings</strong> &rarr; <strong>Integrations</strong></>],
              ].map(([num, text], i) => (
                <React.Fragment key={i}>
                  {i > 0 && <div style={{ display: 'flex', justifyContent: 'center', padding: '2px 0' }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg></div>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 9 }}>
                    <div style={{ width: 22, height: 22, borderRadius: 6, background: 'rgba(124,58,237,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#a78bfa', flexShrink: 0 }}>{num}</div>
                    <span style={{ fontSize: 12, color: 'var(--text2)' }}>{text}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <a href="https://claude.ai/settings" target="_blank" rel="noreferrer" style={{ flex: 1, display: 'block', textAlign: 'center', padding: 10, background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 9, color: '#a78bfa', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>Open Claude.ai settings &rarr;</a>
              <button className="btn-ghost" style={{ fontSize: 11 }} onClick={() => setView('step2')}>I'm there &rarr;</button>
            </div>
          </div>
        )}

        {/* Step 2 */}
        {view === 'step2' && (
          <div style={S.card}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 20 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#a78bfa', flexShrink: 0 }}>2</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3 }}>Add Skilflow as a connector</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5 }}>In Integrations, click "Add integration" and paste the Skilflow connector URL</div>
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>Connector URL — copy and paste this</div>
              <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '11px 14px', fontFamily: "'SF Mono','Fira Code',monospace", fontSize: 12, color: '#a78bfa', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <span>https://mcp.skilflow.ai/mcp</span>
                <CopyBtn text="https://mcp.skilflow.ai/mcp" />
              </div>
            </div>
            <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)', borderRadius: 8, padding: '10px 12px', marginBottom: 16, display: 'flex', gap: 8 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <div style={{ fontSize: 11.5, color: 'var(--text2)', lineHeight: 1.5 }}>Claude.ai will ask you to sign in to Skilflow to authorize the connection. Use your Skilflow email and password.</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-ghost" style={{ fontSize: 11 }} onClick={() => setView('step1')}>&larr; Back</button>
              <button className="btn-primary" style={{ flex: 1, fontSize: 12, padding: '9px 20px' }} onClick={() => setView('step3')}>Connected — next step &rarr;</button>
            </div>
          </div>
        )}

        {/* Step 3 */}
        {view === 'step3' && (
          <div style={S.card}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 20 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#a78bfa', flexShrink: 0 }}>3</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3 }}>Test the connection</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5 }}>Open a new Claude conversation and send this message to confirm your skills are connected</div>
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>Test message — copy and send to Claude</div>
              <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '11px 14px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ fontFamily: "'SF Mono',monospace", fontSize: 12, color: 'var(--text2)', lineHeight: 1.5 }}>"List my Skilflow skills"</span>
                <div style={{ alignSelf: 'flex-end' }}><CopyBtn text="List my Skilflow skills" /></div>
              </div>
            </div>
            <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#10b981', marginBottom: 4 }}>What you should see</div>
              <div style={{ fontSize: 11.5, color: 'var(--text2)', lineHeight: 1.5 }}>Claude will respond with a list of your skills from Skilflow. If it works — you're all set.</div>
            </div>
            <button className="btn-primary" style={{ width: '100%', fontSize: 13, padding: '10px 20px' }} onClick={() => setView('done')}>It worked — I can see my skills &#10003;</button>
            <button className="btn-ghost" style={{ width: '100%', marginTop: 8, fontSize: 11 }} onClick={() => setView('trouble')}>Something went wrong</button>
          </div>
        )}

        {/* Done */}
        {view === 'done' && (
          <div style={{ ...S.card, textAlign: 'center' }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" style={{ animation: 'checkPop 0.3s ease' }}><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: -0.3, marginBottom: 6 }}>You're connected</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 20 }}>Skilflow is now live inside Claude. Try these prompts to get started.</div>
            <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, textAlign: 'left', marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>Try these in Claude</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  ['"Show me my [skill name] skill"', 'Show me my skill'],
                  ['"Update my tone skill to be more concise"', 'Update my tone skill to be more concise'],
                  ['"Create a new skill for writing product descriptions"', 'Create a new skill for writing product descriptions'],
                ].map(([display, copy], i) => (
                  <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '11px 14px', fontFamily: "'SF Mono',monospace", fontSize: 11, color: '#a78bfa', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    {display}<CopyBtn text={copy} />
                  </div>
                ))}
              </div>
            </div>
            <button className="btn-primary" style={{ fontSize: 13, padding: '10px 24px' }} onClick={() => navigate('/')}>Go to your dashboard &rarr;</button>
          </div>
        )}

        {/* Trouble */}
        {view === 'trouble' && (
          <div style={S.card}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Common issues</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {[
                { title: "Can't find the integration option", desc: "Integrations require a Claude Pro or Team plan. Make sure you're signed in at claude.ai with a paid account." },
                { title: "Login failed during authorization", desc: "Use the same email and password you use to sign in to app.skilflow.ai." },
                { title: "Connected but no skills showing", desc: "Make sure your team has skills added to a project you have access to." },
              ].map((item, i) => (
                <div key={i} style={{ padding: '10px 12px', background: 'var(--surface2)', borderRadius: 8, border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#f59e0b', marginBottom: 3 }}>{item.title}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text3)', lineHeight: 1.5 }}>{item.desc}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-ghost" style={{ fontSize: 11 }} onClick={() => setView('step3')}>&larr; Try again</button>
              <a href="mailto:hello@skilflow.ai?subject=MCP connection issue" style={{ flex: 1, display: 'block', textAlign: 'center', padding: 9, background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 9, color: '#a78bfa', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>Contact support</a>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
